import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../../../redux/store";
import { fetchPosts, upsertPost } from "../../../../redux/blogSlice";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bold, Italic, Underline, Link, Image as ImageIcon,
  AlignLeft, AlignCenter, AlignRight,
  List, ListOrdered, Minus, Camera,
  CheckCircle, AlertCircle, Loader2, X, ChevronDown,
} from "lucide-react";

/* ─────────────────────────────────────────────────────────
   FONTS
───────────────────────────────────────────────────────── */
const FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700&family=Montserrat:wght@300;400;500;600;700&display=swap');
`;

/* ─────────────────────────────────────────────────────────
   THEME
───────────────────────────────────────────────────────── */
const C = {
  paper:        "#ffffff",
  surface:      "#f7f7f7",
  ink:          "#0a0a0a",
  inkSoft:      "#2a2a2a",
  inkMid:       "#6b6b6b",
  inkLight:     "#a3a3a3",
  border:       "#e0e0e0",
  success:      "#1a7a3a",
  successBg:    "#f0faf4",
  successBorder:"#b8dfc8",
  danger:       "#b22222",
  dangerBg:     "#fff5f5",
  dangerBorder: "#f0c8c8",
  serif:        "'Cormorant Garamond', Georgia, serif",
  sans:         "'Montserrat', sans-serif",
};

/* ─────────────────────────────────────────────────────────
   TOOLBAR BUTTON
───────────────────────────────────────────────────────── */
const TBtn: React.FC<{
  title: string;
  onClick: (e: React.MouseEvent) => void;
  active?: boolean;
  children: React.ReactNode;
}> = ({ title, onClick, active, children }) => (
  <button
    title={title}
    onMouseDown={e => { e.preventDefault(); onClick(e); }}
    style={{
      width:30, height:30,
      display:"flex", alignItems:"center", justifyContent:"center",
      background: active ? C.ink : "transparent",
      color:      active ? "#fff" : C.inkMid,
      border:     `1px solid ${active ? C.ink : C.border}`,
      cursor:"pointer", transition:"all 0.15s", flexShrink:0,
    }}
    className="ep-tbtn"
  >
    {children}
  </button>
);

const TDivider = () => (
  <div style={{ width:1, height:20, background:C.border, margin:"0 2px", flexShrink:0 }}/>
);

/* ═══════════════════════════════════════════════════════════
   MAIN
════════════════════════════════════════════════════════════ */
const EditPost: React.FC = () => {
  const { postId }  = useParams();
  const dispatch    = useDispatch<AppDispatch>();
  const { posts }   = useSelector((state: RootState) => state.blog);

  /* ── original state ── */
  const [title,         setTitle]         = useState("");
  const [slug,          setSlug]          = useState("");
  const [description,   setDescription]   = useState("");
  const [content,       setContent]       = useState("");
  const [featuredImage, setFeaturedImage] = useState("");
  const [loading,       setLoading]       = useState(false);

  /* ── UI state ── */
  const [toast,        setToast]        = useState<{ type:"success"|"danger"; text:string } | null>(null);
  const [linkPopup,    setLinkPopup]    = useState(false);
  const [linkUrl,      setLinkUrl]      = useState("");
  const [focused,      setFocused]      = useState(false);
  const [wordCount,    setWordCount]    = useState(0);
  const [charCount,    setCharCount]    = useState(0);

  const editorRef       = useRef<HTMLDivElement>(null);
  const fileInputRef    = useRef<HTMLInputElement>(null);
  const featuredInputRef = useRef<HTMLInputElement>(null);
  const linkInputRef    = useRef<HTMLInputElement>(null);

  /* ── original useEffects ── */
  useEffect(() => { dispatch(fetchPosts()); }, [dispatch]);

  useEffect(() => {
    const post = posts.find(p => p.id === postId);
    if (post) {
      setTitle(post.title);
      setSlug(post.slug);
      setDescription(post.description || "");
      setContent(post.content || "");
      setFeaturedImage(post.featuredImage || "");
      // Populate editor after mount
      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.innerHTML = post.content || "";
          updateCounts();
        }
      }, 50);
    }
  }, [postId, posts]);

  /* ── original handleUpdate ── */
  const handleUpdate = async () => {
    if (!title || !slug) { flash("danger","Title and slug are required."); return; }
    setLoading(true);
    await dispatch(upsertPost({ id: postId, title, slug, description, content, featuredImage }));
    setLoading(false);
    flash("success", "Post updated successfully!");
  };

  /* ── original format ── */
  const format = (command: string, value = "") => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  /* ── original insertLink (prompt replaced with popup) ── */
  const handleLinkInsert = () => {
    if (!linkUrl.trim()) return;
    document.execCommand("createLink", false, linkUrl);
    setLinkUrl(""); setLinkPopup(false);
    editorRef.current?.focus();
  };

  /* ── original insertImages ── */
  const insertImages = (files: FileList) => {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return;
    const range = selection.getRangeAt(0);
    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = document.createElement("img");
        img.src = reader.result as string;
        img.style.cssText = "max-width:100%;border-radius:4px;margin:12px 0;display:block;";
        const currentRange = range.cloneRange();
        currentRange.deleteContents();
        currentRange.insertNode(img);
        currentRange.setStartAfter(img);
        currentRange.setEndAfter(img);
        selection.removeAllRanges();
        selection.addRange(currentRange);
      };
      reader.readAsDataURL(file);
    });
  };

  /* ── original handleFeaturedImageSelect ── */
  const handleFeaturedImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setFeaturedImage(reader.result as string);
    reader.readAsDataURL(file);
  };

  const updateCounts = () => {
    const text = editorRef.current?.innerText || "";
    setCharCount(text.length);
    setWordCount(text.trim() ? text.trim().split(/\s+/).length : 0);
  };

  const flash = (type:"success"|"danger", text:string) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  /* ─── RENDER ─── */
  return (
    <div style={{ background:C.paper, fontFamily:C.sans, color:C.ink }}>
      <style>{FONT_CSS + EP_CSS}</style>

      {/* ── TITLE STRIP ── */}
      <div style={S.titleStrip}>
        <div style={{ height:2, background:C.ink, marginBottom:16 }}/>
        <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", flexWrap:"wrap", gap:8, marginBottom:16 }}>
          <div>
            <p style={S.eye}>CONTENT MANAGEMENT</p>
            <h1 style={S.title}>Edit Post</h1>
          </div>
          <div style={{ display:"flex", gap:8, paddingBottom:4 }}>
            <span style={S.metaPill}>{wordCount} words</span>
            <span style={S.metaPill}>{charCount} chars</span>
          </div>
        </div>
        <div style={{ height:1, background:C.border }}/>
      </div>

      <div style={S.body}>

        {/* ── TOAST ── */}
        <AnimatePresence>
          {toast && (
            <motion.div key="toast"
              initial={{ opacity:0, y:-8 }} animate={{ opacity:1, y:0 }}
              exit={{ opacity:0, y:-8 }} transition={{ duration:0.22 }}
              style={{
                display:"flex", alignItems:"center", gap:10,
                padding:"12px 16px", marginBottom:24,
                background:toast.type==="success"?C.successBg:C.dangerBg,
                border:`1px solid ${toast.type==="success"?C.successBorder:C.dangerBorder}`,
              }}>
              {toast.type==="success"
                ? <CheckCircle size={14} color={C.success}/>
                : <AlertCircle size={14} color={C.danger}/>}
              <span style={{ fontSize:12, fontWeight:500, flex:1,
                color:toast.type==="success"?C.success:C.danger }}>
                {toast.text}
              </span>
              <button onClick={()=>setToast(null)}
                style={{ background:"none", border:"none", cursor:"pointer", color:C.inkMid }}>
                <X size={12}/>
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="ep-layout">

          {/* ════════════════════════════
              LEFT — EDITOR
          ════════════════════════════ */}
          <div style={{ display:"flex", flexDirection:"column", gap:20 }}>

            {/* Title */}
            <div style={S.field}>
              <label style={S.label}>POST TITLE <span style={{ color:C.danger }}>*</span></label>
              <input
                type="text"
                placeholder="Post title…"
                value={title}
                onChange={e => {
                  setTitle(e.target.value);
                  setSlug(e.target.value.toLowerCase().replace(/\s+/g,"-")); // original logic
                }}
                style={S.titleInput}
                className="ep-input ep-title-input"
              />
            </div>

            {/* Slug */}
            <div style={S.field}>
              <label style={S.label}>URL SLUG <span style={{ color:C.danger }}>*</span></label>
              <div style={S.slugWrap}>
                <span style={S.slugPrefix}>site.com/</span>
                <input
                  type="text"
                  value={slug}
                  onChange={e => setSlug(e.target.value)}
                  style={S.slugInput}
                  className="ep-input"
                />
              </div>
            </div>

            {/* Description */}
            <div style={S.field}>
              <label style={S.label}>DESCRIPTION / EXCERPT</label>
              <textarea
                placeholder="Short summary for listings and SEO…"
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={3}
                style={S.textarea}
                className="ep-input"
              />
            </div>

            {/* Content editor */}
            <div style={S.field}>
              <label style={S.label}>ARTICLE CONTENT <span style={{ color:C.danger }}>*</span></label>

              {/* Toolbar */}
              <div style={S.toolbar} className="ep-toolbar">
                <TBtn title="Bold"      onClick={()=>format("bold")}>      <Bold size={13}/></TBtn>
                <TBtn title="Italic"    onClick={()=>format("italic")}>    <Italic size={13}/></TBtn>
                <TBtn title="Underline" onClick={()=>format("underline")}> <Underline size={13}/></TBtn>
                <TDivider/>
                <TBtn title="Align left"   onClick={()=>format("justifyLeft")}>   <AlignLeft size={13}/></TBtn>
                <TBtn title="Align center" onClick={()=>format("justifyCenter")}> <AlignCenter size={13}/></TBtn>
                <TBtn title="Align right"  onClick={()=>format("justifyRight")}>  <AlignRight size={13}/></TBtn>
                <TDivider/>
                <TBtn title="Bullet list"   onClick={()=>format("insertUnorderedList")}> <List size={13}/></TBtn>
                <TBtn title="Numbered list" onClick={()=>format("insertOrderedList")}>   <ListOrdered size={13}/></TBtn>
                <TDivider/>
                <TBtn title="H2" onClick={()=>format("formatBlock","h2")}>
                  <span style={{ fontSize:10, fontWeight:700 }}>H2</span>
                </TBtn>
                <TBtn title="H3" onClick={()=>format("formatBlock","h3")}>
                  <span style={{ fontSize:10, fontWeight:700 }}>H3</span>
                </TBtn>
                <TDivider/>

                {/* Link popup */}
                <div style={{ position:"relative" }}>
                  <TBtn title="Insert link"
                    onClick={()=>{ setLinkPopup(v=>!v); setTimeout(()=>linkInputRef.current?.focus(),40); }}
                    active={linkPopup}>
                    <Link size={13}/>
                  </TBtn>
                  <AnimatePresence>
                    {linkPopup && (
                      <motion.div
                        initial={{ opacity:0, y:-4 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}
                        style={{ position:"absolute", top:"110%", left:0, zIndex:200,
                          display:"flex", gap:6, alignItems:"center",
                          background:C.paper, border:`1px solid ${C.border}`,
                          padding:"7px 10px", boxShadow:"0 4px 20px rgba(0,0,0,0.08)",
                          minWidth:280,
                        }}>
                        <input ref={linkInputRef} type="url" placeholder="https://example.com"
                          value={linkUrl} onChange={e=>setLinkUrl(e.target.value)}
                          onKeyDown={e=>e.key==="Enter"&&handleLinkInsert()}
                          style={{ flex:1, background:C.surface, border:`1px solid ${C.border}`,
                            padding:"7px 10px", fontSize:11, color:C.ink,
                            fontFamily:C.sans, outline:"none" }}
                          className="ep-input"
                        />
                        <button onClick={handleLinkInsert}
                          style={{ background:C.ink, color:"#fff", border:"none",
                            padding:"7px 12px", fontSize:9, fontWeight:700,
                            letterSpacing:"0.14em", cursor:"pointer", fontFamily:C.sans }}>
                          INSERT
                        </button>
                        <button onClick={()=>setLinkPopup(false)}
                          style={{ background:"transparent", border:`1px solid ${C.border}`,
                            width:28, height:28, display:"flex", alignItems:"center",
                            justifyContent:"center", cursor:"pointer", color:C.inkMid }}>
                          <X size={11}/>
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Inline image */}
                <label title="Insert image" style={S.tbtnLabel}>
                  <ImageIcon size={13}/>
                  <input type="file" accept="image/*" multiple hidden
                    ref={fileInputRef}
                    onChange={e=>{ if(e.target.files) insertImages(e.target.files); e.target.value=""; }}
                  />
                </label>

                <TDivider/>
                <TBtn title="Remove formatting" onClick={()=>format("removeFormat")}>
                  <Minus size={13}/>
                </TBtn>
              </div>

              {/* Editable area */}
              <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onInput={() => { setContent(editorRef.current?.innerHTML||""); updateCounts(); }}
                onFocus={()=>setFocused(true)}
                onBlur={()=>setFocused(false)}
                style={{
                  ...S.editor,
                  border:`1px solid ${focused?C.ink:C.border}`,
                }}
                className="ep-editor"
                data-placeholder="Start editing your post here…"
              />

              <div style={S.countBar}>
                <span>{wordCount} words · {charCount} characters</span>
                <span>{Math.max(1,Math.round(wordCount/220))} min read</span>
              </div>
            </div>

            {/* Update button */}
            <motion.button
              whileTap={{ scale:0.97 } as any}
              onClick={handleUpdate}
              disabled={loading}
              style={{
                ...S.updateBtn,
                opacity: loading ? 0.65 : 1,
                cursor:  loading ? "not-allowed" : "pointer",
              }}
              className="ep-update-btn"
            >
              {loading
                ? <><Loader2 size={15} className="ep-spin" style={{ marginRight:8 }}/> UPDATING…</>
                : "UPDATE POST"
              }
            </motion.button>
          </div>

          {/* ════════════════════════════
              RIGHT — SIDEBAR
          ════════════════════════════ */}
          <div style={S.sidebar}>

            {/* Featured image */}
            <div style={S.sideCard}>
              <p style={S.sideCardTitle}>FEATURED IMAGE</p>
              {featuredImage ? (
                <div style={{ position:"relative" }}>
                  <img src={featuredImage} alt="Featured"
                    style={{ width:"100%", display:"block", maxHeight:180, objectFit:"cover" }}/>
                  <button onClick={()=>setFeaturedImage("")}
                    style={{ position:"absolute", top:8, right:8,
                      background:"rgba(0,0,0,0.6)", border:"none",
                      width:24, height:24, borderRadius:"50%",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      cursor:"pointer", color:"#fff" }}>
                    <X size={12}/>
                  </button>
                </div>
              ) : (
                <label style={S.featuredUpload}>
                  <Camera size={22} color={C.inkLight} strokeWidth={1.5}/>
                  <span style={{ fontSize:11, color:C.inkLight, marginTop:6 }}>Upload cover image</span>
                  <span style={{ fontSize:9, color:C.inkLight, marginTop:2 }}>JPG, PNG, WEBP</span>
                  <input type="file" accept="image/*" hidden
                    ref={featuredInputRef}
                    onChange={handleFeaturedImageSelect}
                  />
                </label>
              )}
            </div>

            {/* Publish checklist */}
            <div style={S.sideCard}>
              <p style={S.sideCardTitle}>UPDATE CHECKLIST</p>
              <div style={{ display:"flex", flexDirection:"column", gap:9 }}>
                {[
                  { label:"Title",             done: title.trim().length > 0 },
                  { label:"Slug",              done: slug.trim().length > 0 },
                  { label:"Description",       done: description.trim().length > 0 },
                  { label:"Content",           done: wordCount > 0 },
                  { label:"Featured image",    done: !!featuredImage },
                ].map(item => (
                  <div key={item.label} style={{ display:"flex", alignItems:"center", gap:9 }}>
                    <div style={{
                      width:15, height:15, borderRadius:"50%", flexShrink:0,
                      background:item.done ? C.ink : "transparent",
                      border:`1.5px solid ${item.done ? C.ink : C.border}`,
                      display:"flex", alignItems:"center", justifyContent:"center",
                      transition:"all 0.2s",
                    }}>
                      {item.done && <div style={{ width:4, height:4, borderRadius:"50%", background:"#fff" }}/>}
                    </div>
                    <span style={{ fontSize:11, color:item.done?C.ink:C.inkLight, fontFamily:C.sans, transition:"color 0.2s" }}>
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* SEO preview */}
            <div style={S.sideCard}>
              <p style={S.sideCardTitle}>SEO PREVIEW</p>
              <div style={{ background:C.surface, padding:"12px 14px" }}>
                <p style={{ fontSize:14, fontWeight:500, color:"#1a0dab", marginBottom:3 }}>
                  {title || "Post title"}
                </p>
                <p style={{ fontSize:11, color:C.success, marginBottom:4 }}>
                  site.com/{slug || "your-slug"}
                </p>
                <p style={{ fontSize:11, color:C.inkMid, lineHeight:1.6 }}>
                  {description || "Your description will appear here in search results."}
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default EditPost;

/* ─────────────────────────────────────────────────────────
   STYLES
───────────────────────────────────────────────────────── */
const S: { [key: string]: React.CSSProperties } = {
  titleStrip: { padding:"20px 20px 0" },
  eye:        { fontSize:9, letterSpacing:"0.3em", color:C.inkMid, fontFamily:C.sans, marginBottom:6 },
  title:      { fontFamily:C.serif, fontSize:"clamp(26px,4vw,42px)" as any, fontWeight:600, color:C.ink, lineHeight:1, margin:0, letterSpacing:"-0.01em" },
  metaPill:   { fontSize:9, letterSpacing:"0.16em", color:C.inkMid, border:`1px solid ${C.border}`, padding:"3px 10px", fontFamily:C.sans },

  body: { maxWidth:1160, margin:"0 auto", padding:"28px 20px 60px" },

  field:    { display:"flex", flexDirection:"column", gap:7 },
  label:    { fontSize:9, letterSpacing:"0.22em", color:C.inkMid, fontFamily:C.sans, fontWeight:600 },

  titleInput: {
    width:"100%", background:C.paper, border:`1px solid ${C.border}`,
    padding:"14px 16px", fontSize:18, fontWeight:600, color:C.ink,
    fontFamily:C.serif, outline:"none", boxSizing:"border-box" as const,
    letterSpacing:"-0.01em", transition:"border-color 0.2s",
  },
  slugWrap:   { display:"flex", alignItems:"center", border:`1px solid ${C.border}`, background:C.paper },
  slugPrefix: { fontSize:11, color:C.inkLight, padding:"0 12px", flexShrink:0, borderRight:`1px solid ${C.border}`, lineHeight:"42px" },
  slugInput:  { flex:1, background:"transparent", border:"none", outline:"none", padding:"11px 12px", fontSize:12, color:C.ink, fontFamily:"'Courier New',monospace", letterSpacing:"0.02em" },
  textarea: {
    width:"100%", background:C.paper, border:`1px solid ${C.border}`,
    padding:"11px 14px", fontSize:13, color:C.ink,
    fontFamily:C.sans, fontWeight:300, outline:"none",
    resize:"vertical", boxSizing:"border-box" as const,
    transition:"border-color 0.2s", lineHeight:1.75,
  },

  toolbar: {
    display:"flex", flexWrap:"wrap", alignItems:"center", gap:3,
    padding:"7px 10px", background:C.surface,
    border:`1px solid ${C.border}`, borderBottom:"none",
  },
  tbtnLabel: {
    width:30, height:30, display:"flex", alignItems:"center", justifyContent:"center",
    cursor:"pointer", color:C.inkMid, border:`1px solid ${C.border}`,
    background:"transparent", transition:"all 0.15s", flexShrink:0,
  } as React.CSSProperties,

  editor: {
    minHeight:320, padding:"18px",
    fontSize:15, color:C.inkSoft, lineHeight:1.85,
    fontFamily:C.sans, fontWeight:300, outline:"none",
    background:C.paper, transition:"border-color 0.2s",
    boxSizing:"border-box" as const,
  },
  countBar: {
    display:"flex", justifyContent:"space-between",
    fontSize:9, color:C.inkLight, letterSpacing:"0.1em", padding:"5px 0 2px",
  },

  updateBtn: {
    display:"inline-flex", alignItems:"center", justifyContent:"center",
    background:C.ink, color:"#fff", border:"none",
    padding:"13px 32px", fontSize:10, fontWeight:700,
    letterSpacing:"0.22em", fontFamily:C.sans,
    transition:"opacity 0.2s", width:"100%",
  },

  sidebar:       { position:"sticky" as const, top:20, display:"flex", flexDirection:"column", gap:16 },
  sideCard:      { border:`1px solid ${C.border}`, padding:"16px 16px" },
  sideCardTitle: { fontSize:9, letterSpacing:"0.25em", color:C.ink, fontFamily:C.sans, fontWeight:700, marginBottom:14 },
  featuredUpload: {
    display:"flex", flexDirection:"column" as const, alignItems:"center",
    justifyContent:"center", border:`1px dashed ${C.border}`,
    padding:"28px 16px", cursor:"pointer", textAlign:"center" as const,
    background:C.surface, transition:"border-color 0.2s",
  } as React.CSSProperties,
};

/* ─────────────────────────────────────────────────────────
   CSS
───────────────────────────────────────────────────────── */
const EP_CSS = `
  * { box-sizing: border-box; }
  ::-webkit-scrollbar { width: 0; }

  .ep-input:focus        { border-color: #0a0a0a !important; }
  .ep-title-input:focus  { border-color: #0a0a0a !important; }
  input::placeholder     { color: #a3a3a3; font-size: 12px; }
  textarea::placeholder  { color: #a3a3a3; font-size: 12px; line-height: 1.7; }

  .ep-editor:empty::before {
    content: attr(data-placeholder);
    color: #a3a3a3; font-size: 15px;
    font-family: 'Montserrat', sans-serif; font-weight: 300;
    pointer-events: none;
  }
  .ep-editor h2 { font-family: 'Cormorant Garamond',serif; font-size: clamp(20px,3vw,30px); font-weight:600; color:#0a0a0a; margin:22px 0 10px; line-height:1.15; }
  .ep-editor h3 { font-family: 'Cormorant Garamond',serif; font-size: clamp(17px,2.5vw,24px); font-weight:500; color:#2a2a2a; margin:18px 0 8px; }
  .ep-editor p  { margin-bottom:12px; line-height:1.85; }
  .ep-editor a  { color:#0a0a0a; text-decoration:underline; text-underline-offset:3px; }
  .ep-editor ul, .ep-editor ol { margin:10px 0 14px 20px; }
  .ep-editor li { margin-bottom:5px; line-height:1.7; }
  .ep-editor img { max-width:100%; border-radius:4px; margin:12px 0; display:block; }
  .ep-editor blockquote { border-left:2px solid #0a0a0a; padding:4px 0 4px 16px; margin:16px 0; font-style:italic; color:#2a2a2a; }

  .ep-tbtn:hover { border-color: #0a0a0a !important; color: #0a0a0a !important; }

  @keyframes ep-spin { from{transform:rotate(0deg);} to{transform:rotate(360deg);} }
  .ep-spin { animation: ep-spin 1s linear infinite; }

  /* ── Layout ── */
  .ep-layout {
    display: grid;
    grid-template-columns: 1fr 280px;
    gap: 32px;
    align-items: start;
  }

  @media (max-width: 720px) {
    .ep-layout {
      grid-template-columns: 1fr !important;
      gap: 24px !important;
    }
    .ep-layout > div:last-child {
      position: relative !important;
      top: 0 !important;
    }
    .ep-update-btn { width: 100% !important; }
    .ep-toolbar { padding: 6px 8px !important; gap: 2px !important; }
    .ep-editor { min-height: 220px !important; }
  }
`;